import type {
  ModStatus,
  PersonListOut,
  PublicationListOut,
} from '@/api/generated/schemas';

export type PublicationFormat = 'ebook' | 'audio';

/** Formats derive from part presence, not a dedicated field. */
export const publicationFormats = (
  publication: Pick<PublicationListOut, 'ebook' | 'audio'> | undefined
): PublicationFormat[] => {
  const formats: PublicationFormat[] = [];
  if (publication?.ebook) formats.push('ebook');
  if (publication?.audio) formats.push('audio');
  return formats;
};

export interface PublicationFormatStatus {
  format: PublicationFormat;
  /** `null` when the publication has no such format at all. */
  modStatus: ModStatus | null;
}

const FORMATS: PublicationFormat[] = ['ebook', 'audio'];

/** Both formats, always — each with its own moderation status,
 * independent of the publication's. */
export const publicationFormatStatuses = (
  publication: Pick<PublicationListOut, 'ebook' | 'audio'> | undefined
): PublicationFormatStatus[] =>
  FORMATS.map((format) => ({
    format,
    modStatus: publication?.[format]?.mod_status ?? null,
  }));

/** The app's hero CTA is hardcoded to "listen": the audio asset's own
 * mod_status decides, not the publication's. */
export const hasPlayableAudio = (
  publication: Pick<PublicationListOut, 'audio'> | undefined
): boolean => publication?.audio?.mod_status === 'enabled';

/** `43h 14min`; app-style, unit dropped when its part is zero. */
export const formatAudioDuration = (
  minutes: number | null | undefined
): string | null => {
  if (minutes == null || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

export type PublicationIssue = 'deleted' | 'mod_status';

/** Advisory row diagnostics; `undefined` means the lookup resolved to
 * nothing, so the publication is gone. Callers must suppress these while
 * lookups are still loading, and add their own surface-specific rules. */
export const publicationIssues = (
  publication: PublicationListOut | undefined
): PublicationIssue[] => {
  if (!publication) return ['deleted'];
  return publication.mod_status === 'enabled' ? [] : ['mod_status'];
};

/** Author ids in credit order across the given publications, deduped. */
export const orderedAuthorIds = (
  publications: (PublicationListOut | undefined)[]
): number[] => {
  const ids: number[] = [];
  const seen = new Set<number>();
  for (const publication of publications) {
    const credits = [...(publication?.publication_authors ?? [])].sort(
      (a, b) => a.position - b.position
    );
    for (const credit of credits) {
      if (seen.has(credit.author_id)) continue;
      seen.add(credit.author_id);
      ids.push(credit.author_id);
    }
  }
  return ids;
};

/** Credit-order author names; unresolved authors are skipped. */
export const authorNames = (
  publication: PublicationListOut | undefined,
  authorById: Map<number, PersonListOut>
): string[] =>
  orderedAuthorIds([publication])
    .map((id) => authorById.get(id)?.name)
    .filter((name): name is string => Boolean(name));
